import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, BookOpen, FileText, Info, Search, ShoppingBag, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const ICON = { page: FileText, rule: BookOpen, info: Info, package: ShoppingBag };
const LABEL = { page: "Página", rule: "Regra", info: "Informação", package: "Loja" };

export function SiteSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const id = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => { setResults(data.results); setActive(0); }).catch(() => setResults([])), 180);
    return () => clearTimeout(id);
  }, [q, open]);

  const go = (r) => {
    setOpen(false);
    setQ("");
    if (r.external) window.open(r.external, "_blank", "noopener");
    else navigate(r.url);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && results[active]) go(results[active]);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex h-8 items-center gap-2 rounded-full border border-eden-border bg-eden-surface px-3 text-xs text-eden-secondary transition-colors hover:border-eden-secondary hover:text-eden-text" aria-label="Pesquisar no site" data-testid="site-search-button">
        <Search size={15} aria-hidden="true" />
        <span className="hidden lg:inline">Pesquisar</span>
        <kbd className="hidden rounded-sm border border-eden-border px-1.5 font-mono text-[10px] text-eden-muted lg:inline">Ctrl K</kbd>
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <DialogContent className="top-[12%] max-w-2xl translate-y-0 gap-0 border-eden-border !bg-[#0e0f12] p-0 [&>button]:hidden" onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }} data-testid="site-search-dialog">
          <DialogTitle className="sr-only">Pesquisar no site</DialogTitle>
          <div className="flex items-center gap-3 border-b border-eden-border px-4">
            <Search size={18} className="shrink-0 text-eden-orange" aria-hidden="true" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Procura regras, informações, pacotes da loja…" className="h-14 flex-1 bg-transparent text-base text-eden-text placeholder:text-eden-muted focus:outline-none" aria-label="Termo de pesquisa" data-testid="site-search-input" />
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost h-8 px-2" aria-label="Fechar pesquisa" data-testid="site-search-close"><X size={16} /></button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2" role="listbox" aria-label="Resultados" data-testid="site-search-results">
            {q.trim().length < 2 && <p className="px-3 py-8 text-center text-sm text-eden-muted">Escreve pelo menos 2 letras. Ex.: "whitelist", "metagaming", "VIP".</p>}
            {q.trim().length >= 2 && results.length === 0 && <p className="px-3 py-8 text-center text-sm text-eden-muted" data-testid="site-search-empty">Sem resultados para "{q}".</p>}
            {results.map((r, i) => {
              const Icon = ICON[r.type] || FileText;
              return (
                <button key={`${r.type}-${r.url}-${r.title}`} type="button" role="option" aria-selected={i === active} onMouseEnter={() => setActive(i)} onClick={() => go(r)} className={`flex w-full items-start gap-3 rounded-md px-3 py-3 text-left transition-colors ${i === active ? "bg-eden-surface" : "hover:bg-eden-surface/60"}`} data-testid={`site-search-result-${i}`}>
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-sm border border-eden-border text-eden-orange"><Icon size={15} aria-hidden="true" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-eden-text">{r.title}</span>
                      <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-eden-muted">{LABEL[r.type]}{r.category ? ` · ${r.category}` : ""}</span>
                    </span>
                    {r.snippet && <span className="mt-0.5 line-clamp-2 block text-xs text-eden-secondary">{r.snippet}</span>}
                  </span>
                  {r.external && <ArrowUpRight size={14} className="mt-1 shrink-0 text-eden-muted" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
